import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Icon } from '../components/sp-primitives';
import { PShell, useApp } from '../components/sp-shell';

function buildDefaultTemplateData(users) {
  return {
    name: '',
    description: '',
    paymentType: 'mass',
    fileType: 'CSV',
    delimiter: ';',
    reconciliation: 'BY_CPF',
    identifierFieldMode: 'COLUMN_NAME',
    identifierFieldLocation: '',
    allowedUsers: users.slice(0, 2).map((u) => String(u.id)),
    ativo: true,
  };
}

function TemplateFormBody({ initial, users, onCancel, onSave }) {
  const [data, setData] = useState(() => initial || buildDefaultTemplateData(users));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setData(initial || buildDefaultTemplateData(users));
    setErrors({});
  }, [initial, users]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const toggleUser = (id) => set('allowedUsers', data.allowedUsers.includes(id) ? data.allowedUsers.filter((x) => x !== id) : [...data.allowedUsers, id]);
  const userIds = useMemo(() => users.map((u) => String(u.id)), [users]);
  const validateData = (value) => {
    const nextErrors = {};
    if (String(value.name || '').trim().length <= 2) nextErrors.name = true;
    if (value.paymentType === 'mass') {
      if (!String(value.fileType || '').trim()) nextErrors.fileType = true;
      if (!String(value.delimiter || '').trim()) nextErrors.delimiter = true;
      if (!String(value.identifierFieldMode || '').trim()) nextErrors.identifierFieldMode = true;
      if (!String(value.identifierFieldLocation || '').trim()) nextErrors.identifierFieldLocation = true;
    }
    return nextErrors;
  };
  const valid = Object.keys(validateData(data)).length === 0;
  const isMassPayment = data.paymentType === 'mass';
  const sectionNumber = {
    identification: 1,
    paymentType: 2,
    fileFormat: isMassPayment ? 3 : null,
    reconciliation: isMassPayment ? 4 : 3,
    identifierLocation: isMassPayment ? 5 : null,
    allowedUsers: isMassPayment ? 6 : 4,
  };

  const handleSaveClick = async () => {
    const nextErrors = validateData(data);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    await onSave(data);
  };

  return (
    <div className="sp-template-form">
      <div className="sp-template-form__head">
        <div>
          <div className="sp-caps sp-template-form__caps">Modelos · {initial ? 'editar' : 'novo'}</div>
          <h1 className="sp-template-form__title">
            {initial ? `Editar · ${initial.name}` : 'Configurar modelo de pagamento'}
          </h1>
          <div className="sp-template-form__lead">
            Defina como o middleware deve interpretar o arquivo e quem pode usá-lo.
          </div>
        </div>
        <button onClick={onCancel} className="sp-btn sp-btn-ghost"><Icon name="x" size={13} /> Cancelar</button>
      </div>

      {initial && (
        <section className="sp-card sp-template-form__status-card">
          <div className="sp-template-form__status-head">
            <div>
              <div className="sp-template-form__section-title">Status do modelo</div>
              <div className="sp-template-form__section-subtitle">Controla se o modelo aparece disponível na listagem.</div>
            </div>
            <button
              type="button"
              className={`sp-btn ${data.ativo === false ? 'sp-btn-outline' : 'sp-btn-accent'}`}
              onClick={() => set('ativo', data.ativo === false)}
            >
              {data.ativo === false ? 'Inativo' : 'Ativo'}
            </button>
          </div>
        </section>
      )}

      <FormSection number={sectionNumber.identification} title="Identificação" subtitle="Nome visível na lista e descrição interna.">
        <FieldRow>
          <Field label="Nome do modelo" required hint="Ex: Folha Mensal — Matriz">
            <input className={`sp-input${errors.name ? ' is-invalid' : ''}`} value={data.name} onChange={(e) => set('name', e.target.value)} placeholder="Folha Mensal — Matriz" autoFocus />
          </Field>
        </FieldRow>
        <FieldRow>
          <Field label="Descrição" hint="Para o time saber quando usar este modelo.">
            <textarea className="sp-input sp-template-form__textarea" rows={3} value={data.description} onChange={(e) => set('description', e.target.value)}
              placeholder="Folha mensal regular dos colaboradores CLT da matriz. Usar nos dias 25 de cada mês." />
          </Field>
        </FieldRow>
      </FormSection>

      <FormSection number={sectionNumber.paymentType} title="Tipo de pagamento" subtitle="Como o Sienge deve registrar os pedidos gerados a partir deste modelo.">
        <RadioCards value={data.paymentType} onChange={(v) => set('paymentType', v)} options={[
          { value: 'individual', icon: 'user', title: 'Individual', desc: 'Um pedido de pagamento por colaborador. Ideal para rescisões e bônus pontuais.' },
          { value: 'mass', icon: 'users', title: 'Em massa', desc: 'Lote único agrupando vários colaboradores. Ideal para folha mensal e adiantamentos.' },
        ]} />
      </FormSection>

      {data.paymentType === 'mass' && (
        <FormSection number={sectionNumber.fileFormat} title="Formato do arquivo" subtitle="Tipo do arquivo de entrada e como suas colunas estão separadas.">
          <FieldRow cols={2}>
            <Field label="Tipo de arquivo" required>
              <SegmentedToggle invalid={errors.fileType} value={data.fileType} onChange={(v) => set('fileType', v)} options={[
                { value: 'CSV', label: 'CSV', hint: 'Comma-Separated Values' },
                { value: 'TXT', label: 'TXT', hint: 'Texto delimitado' },
              ]} />
            </Field>
            <Field label="Delimitador" required>
              <SegmentedToggle invalid={errors.delimiter} value={data.delimiter} onChange={(v) => set('delimiter', v)} options={[
                { value: ',', label: 'Vírgula', mono: ',' },
                { value: ';', label: 'Ponto-vírgula', mono: ';' },
              ]} />
            </Field>
          </FieldRow>
        </FormSection>
      )}

      <FormSection number={sectionNumber.reconciliation} title="Estratégia de Identificação do credor no Sienge" subtitle="Como o sistema deve casar cada linha do arquivo com o credenciado já cadastrado no Sienge.">
        <RadioCards value={data.reconciliation} onChange={(v) => set('reconciliation', v)} cols={2} options={[
          { value: 'BY_CPF', mono: 'BY_CPF', title: 'Por CPF', desc: 'Busca no Sienge pelo CPF da linha. Recomendado.' },
          { value: 'BY_CNPJ', mono: 'BY_CNPJ', title: 'Por CNPJ', desc: 'Para credenciados PJ (terceirizados, prestadores).' },
        ]} />
      </FormSection>

      {data.paymentType === 'mass' && (
        <FormSection
          number={sectionNumber.identifierLocation}
          title="Localização do Campo Identificador"
          subtitle="Informe onde o sistema deve ler o campo usado para identificar o credor (CPF ou CNPJ) no arquivo."
        >
          <FieldRow cols={2}>
            <Field label="Forma de localização" required>
              <SegmentedToggle
                invalid={errors.identifierFieldMode}
                value={data.identifierFieldMode}
                onChange={(v) => set('identifierFieldMode', v)}
                options={[
                  { value: 'COLUMN_NAME', label: 'Nome da Coluna' },
                  { value: 'COLUMN_POSITION', label: 'Posição da Coluna' },
                ]}
              />
            </Field>
          </FieldRow>
          <FieldRow>
            <Field
              label={data.identifierFieldMode === 'COLUMN_NAME' ? 'Nome da coluna do identificador' : 'Posição da coluna do identificador'}
              required
              hint={data.identifierFieldMode === 'COLUMN_NAME'
                ? 'Exemplo: CPF ou CNPJ no cabeçalho do arquivo.'
                : 'Exemplo: Coluna 3 (numeração iniciando em 1).'}
            >
              <input
                className={`sp-input${errors.identifierFieldLocation ? ' is-invalid' : ''}`}
                value={data.identifierFieldLocation}
                onChange={(e) => set('identifierFieldLocation', e.target.value)}
                placeholder={data.identifierFieldMode === 'COLUMN_NAME' ? 'Ex.: CPF' : 'Ex.: 3'}
              />
            </Field>
          </FieldRow>
        </FormSection>
      )}

      <FormSection number={sectionNumber.allowedUsers} title="Usuários permitidos"
        subtitle={`Apenas pessoas marcadas poderão escolher este modelo na importação. ${data.allowedUsers.length} de ${users.length} selecionados.`}
        right={(
          <div className="sp-template-form__section-actions">
            <button type="button" className="sp-btn sp-btn-ghost sp-template-form__tiny-btn" onClick={() => set('allowedUsers', userIds)}>Selecionar todos</button>
            <button type="button" className="sp-btn sp-btn-ghost sp-template-form__tiny-btn" onClick={() => set('allowedUsers', [])}>Limpar</button>
          </div>
        )}>
        <div className="sp-template-form__users-list">
          {users.map((u, i) => {
            const checked = data.allowedUsers.includes(String(u.id));
            return (
              <label key={u.id} className={`sp-template-form__user-row${checked ? ' is-selected' : ''}${i === 0 ? ' is-first' : ''}`}>
                <input type="checkbox" checked={checked} onChange={() => toggleUser(String(u.id))}
                  className="sp-template-form__user-checkbox" />
                <Avatar name={u.name} size={34} />
                <div className="sp-template-form__user-main">
                  <div className="sp-template-form__user-name">{u.name}</div>
                  <div className="sp-mono sp-template-form__user-email">{u.email}</div>
                </div>
                <span className="sp-chip sp-template-form__user-role">{u.role}</span>
              </label>
            );
          })}
        </div>
      </FormSection>

      <div className="sp-template-form__footer">
        <div className="sp-template-form__footer-hint">
          {valid ? '✓ Pronto para salvar' : ''}
        </div>
        <div className="sp-template-form__footer-actions">
          <button className="sp-btn sp-btn-outline" onClick={onCancel}>Cancelar</button>
          <button className="sp-btn sp-btn-accent" onClick={handleSaveClick}>
            <Icon name="check" size={13} stroke={2.4} /> {initial ? 'Salvar alterações' : 'Criar modelo'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormSection({ number, title, subtitle, right, children }) {
  return (
    <section className="sp-card sp-template-form__section">
      <header className="sp-template-form__section-head">
        <div className="sp-template-form__section-head-main">
          <div className="sp-template-form__section-number">{number}</div>
          <div>
            <div className="sp-template-form__section-title">{title}</div>
            {subtitle && <div className="sp-template-form__section-subtitle">{subtitle}</div>}
          </div>
        </div>
        {right}
      </header>
      <div>{children}</div>
    </section>
  );
}

function FieldRow({ children, cols = 1 }) {
  return <div className={`sp-template-form__field-row cols-${cols}`}>{children}</div>;
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="sp-caps sp-template-form__label">
        {label} {required && <span className="sp-template-form__label-required">*</span>}
      </label>
      {children}
      {hint && <div className="sp-template-form__hint">{hint}</div>}
    </div>
  );
}

function SegmentedToggle({ value, onChange, options, invalid = false }) {
  return (
    <div className={`sp-template-form__segmented${invalid ? ' is-invalid' : ''}`}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} className={`sp-template-form__segmented-btn${on ? ' is-active' : ''}`}>
            <div className="sp-template-form__segmented-label">{o.label}</div>
            {(o.hint || o.mono) && <div className={`${o.mono ? 'sp-mono ' : ''}sp-template-form__segmented-sub`}>{o.mono || o.hint}</div>}
          </button>
        );
      })}
    </div>
  );
}

function RadioCards({ value, onChange, options, cols = 2 }) {
  return (
    <div className={`sp-template-form__radio-grid cols-${cols}`}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={o.value} type="button" onClick={() => onChange(o.value)} className={`sp-template-form__radio-card${on ? ' is-active' : ''}`}>
            <div className={`sp-template-form__radio-dot${on ? ' is-active' : ''}`} />
            <div className="sp-template-form__radio-body">
              <div className="sp-template-form__radio-title-row">
                {o.icon && <span className={`sp-template-form__radio-icon${on ? ' is-active' : ''}`}><Icon name={o.icon} size={13} /></span>}
                <span className="sp-template-form__radio-title">{o.title}</span>
                {o.mono && <span className={`sp-mono sp-template-form__radio-mono${on ? ' is-active' : ''}`}>{o.mono}</span>}
              </div>
              <div className="sp-template-form__radio-desc">{o.desc}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function mapInitialFromTemplate(t) {
  if (!t) return null;
  return {
    id: t.id,
    name: t.n || '',
    description: t.description || '',
    paymentType: t.paymentType || 'mass',
    fileType: t.fileType || 'CSV',
    delimiter: t.sep || ';',
    reconciliation: t.strat || 'BY_CPF',
    identifierFieldMode: t.identifierFieldMode || 'COLUMN_NAME',
    identifierFieldLocation: t.identifierFieldLocation || '',
    allowedUsers: Array.isArray(t.allowedUsers) ? t.allowedUsers.map((x) => String(x)) : [],
    ativo: t.ativo !== false,
  };
}

export function PTemplateForm() {
  const app = useApp();
  const editingParam = app.params?.editing || null;
  const editingRaw = editingParam?.id != null
    ? (app.templates || []).find((t) => String(t.id) === String(editingParam.id)) || editingParam
    : null;
  const editing = mapInitialFromTemplate(editingRaw);
  const apiUsers = (app.users || []).map((u) => ({
    id: String(u.id),
    name: u.name || 'Usuário',
    email: u.email || '',
    role: u.role || 'Operacional',
  }));
  const fallbackUsers = [
    { id: 'u1', name: 'João Silva', email: 'joao.silva@fulltec.com.br', role: 'ADMIN' },
    { id: 'u2', name: 'Ana Beatriz Costa', email: 'ana.costa@fulltec.com.br', role: 'Operacional' },
    { id: 'u3', name: 'Rafael Souza', email: 'rafael.souza@fulltec.com.br', role: 'Financeiro' },
    { id: 'u4', name: 'Marina Lopes', email: 'marina.lopes@fulltec.com.br', role: 'Auditoria' },
    { id: 'u5', name: 'Pedro Henrique M.', email: 'pedro.henrique@fulltec.com.br', role: 'Operacional' },
    { id: 'u6', name: 'Camila Andrade', email: 'camila@fulltec.com.br', role: 'Operacional' },
    { id: 'u7', name: 'Eduardo Lima', email: 'eduardo.lima@fulltec.com.br', role: 'Financeiro' },
  ];
  const users = apiUsers.length ? apiUsers : fallbackUsers;

  return (
    <PShell active="templates">
      <TemplateFormBody
        initial={editing}
        users={users}
        onCancel={() => app.navigate('templates')}
        onSave={async (form) => {
          if (form.paymentType === 'mass') {
            if (!form.fileType || !form.delimiter) {
              app.toast('error', 'Campos obrigatórios', 'Preencha o formato do arquivo para modelo em massa.');
              return;
            }
            if (!form.identifierFieldMode || !String(form.identifierFieldLocation || '').trim()) {
              app.toast('error', 'Campos obrigatórios', 'Preencha a localização do campo identificador para modelo em massa.');
              return;
            }
          }

          const normalizedForm = form.paymentType === 'individual'
            ? {
              ...form,
              fileType: '',
              delimiter: '',
              identifierFieldMode: '',
              identifierFieldLocation: '',
            }
            : form;

          try {
            if (typeof app.saveTemplate === 'function') {
              await app.saveTemplate({
                id: editingRaw?.id ?? editing?.id,
                name: normalizedForm.name,
                description: normalizedForm.description,
                paymentType: normalizedForm.paymentType,
                fileType: normalizedForm.fileType,
                delimiter: normalizedForm.delimiter,
                reconciliation: normalizedForm.reconciliation,
                identifierFieldMode: normalizedForm.identifierFieldMode,
                identifierFieldLocation: normalizedForm.identifierFieldLocation,
                allowedUsers: normalizedForm.allowedUsers,
                ativo: normalizedForm.ativo !== false,
              }, editingRaw || null);
            }
            app.navigate('templates');
            app.toast('success', editing ? 'Modelo atualizado' : 'Modelo criado',
              `${form.name} · ${form.fileType} · ${form.allowedUsers.length} usuários permitidos.`);
          } catch (error) {
            app.toast('error', 'Salvar modelo', error?.message || 'Falha ao salvar no servidor.');
          }
        }}
      />
    </PShell>
  );
}
